import Flex from "components/grid/Flex";
import { useTranslation } from "react-i18next";
import HistoryContainerAndTitle from "./HistoryContainerAndTitle";
import DateAndValueArrayContainer from "./DateAndValueArrayContainer";
import { isAnyPresent, makeHistory } from "../../HistoryUtils";
import ConditionalLabelAndValue from "./ConditionalLabelAndValue";
import ConditionalLabel from "./ConditionalLabel";
import Value from "./Value";

const CardiovascularDiseaseHistory = ({ anamnesisHistory }) => {
    const { t } = useTranslation();
    const history = makeHistory(anamnesisHistory);
    const isVisible = isAnyPresent(anamnesisHistory, [
        "hasEnlargementOfTheAorta",
        "firstCardioDiseaseDate",
        "coronaryArteryDiseaseState",
        "bypassSurgeryData",
        "haveStentImplantationData",
        "haveMiocardialHeartAttackData",
        "haveAtherosclerosisValue",
        "haveAtherosclerosisStentImplantationData",
        "haveStrokeData",
        "haveStrokePermanentRestrictionDescription",
        "haveTransientIschemicAttackData",
        "haveAtherosclerosisLegArteriesStage",
        "haveAtherosclerosisLegArteriesStentImplantationData",
        "havePlumonaryArteryEmbolismData",
        "haveCardiacEjectionFractionPercent",
        "haveCardiacEjectionFractionDescription",
        "haveCardiomypathyDiagnoseValue",
        "haveSufferFromHeartFailureDecompensation",
        "hasAorticInsufficiency",
        "hasAorticStenosis",
        "hasMitralValveInsufficiency",
        "hasMitralValveStenosis",
        "hasPulmonicValveInsufficiency",
        "hasPulmonaryValveStenosis",
        "hasTricuspidValveInsufficiency",
        "hasTricuspidValveStenosis",
        "haveHeartValveTreatmentData",
        "haveCardiacArrhythmiaType",
        "haveResuscitatedData",
        "haveCongenitalHeartDefectType",
        "havePacemakerOrDefibrillatorData",
        "haveMyocardialBiopsyData",
        "coronaryArteryDisease",
        "bypassSurgery",
        "haveStentImplantation",
        "haveMiocardialHeartAttack",
        "haveAtherosclerosis",
        "haveAtherosclerosisStentImplantation",
        "haveStroke",
        "haveStrokePermanentRestriction",
        "haveTransientIschemicAttack",
        "haveAtherosclerosisLegArteries",
        "haveAtherosclerosisLegArteriesStentImplantation",
        "havePlumonaryArteryEmbolism",
        "haveCardiacEjectionFraction",
        "haveCardiomypathy",
        "haveSufferFromHeartFailure",
        "haveValvularHeartDisease",
        "haveHeartValveTreatment",
        "haveCardiacArrhythmia",
        "haveResuscitated",
        "haveCongenitalHeartDefect",
        "havePacemakerOrDefibrillator",
        "haveMyocardialBiopsy",
    ]);
    return (
        <HistoryContainerAndTitle title={t(`anamnesis.cardio.cardiovasclar-disase`)} isVisible={isVisible}>
            <Flex item grow={0} container column style={{ margin: "5px 0px" }}>
                <ConditionalLabelAndValue
                    {...{
                        value: history.getLatestValueOf("firstCardioDiseaseDate"),
                        label: t("anamnesis.cardio.first-cardiovascular-disease"),
                    }}
                />
                <ConditionalLabel
                    {...{
                        value: history.getLatestValueOf("hasEnlargementOfTheAorta"),
                        label: `Z.n. ${t("anamnesis.cardio.enlargement-of-the-aorta")}`,
                    }}
                />
                <ConditionalLabelAndValue
                    {...{
                        switchValue: history.getLatestValueOf("coronaryArteryDisease"),
                        value: history.getLatestValueOf("coronaryArteryDiseaseState")
                            ? t(`anamnesis.cardio.${history.getLatestValueOf("coronaryArteryDiseaseState")}`)
                            : "",
                        label: `Z.n. ${t("anamnesis.cardio.coronary-artery-disease")}`,
                    }}
                />
                <DateAndValueArrayContainer
                    yearOnly
                    indent
                    {...{
                        switchValue: history.getLatestValueOf("bypassSurgery"),
                        array: history.getLatestSubDataRows("bypassSurgeryData"),
                        label: t("anamnesis.cardio.bypass-surgery"),
                        translationKey: "anamnesis.cardio",
                    }}
                />
                <DateAndValueArrayContainer
                    indent
                    yearOnly
                    {...{
                        switchValue: history.getLatestValueOf("haveStentImplantation"),
                        array: history.getLatestSubDataRows("haveStentImplantationData"),
                        label: t("anamnesis.cardio.stent"),
                        suffix: "stent",
                    }}
                />
                <DateAndValueArrayContainer
                    yearOnly
                    {...{
                        switchValue: history.getLatestValueOf("haveMiocardialHeartAttack"),
                        array: history.getLatestSubDataRows("haveMiocardialHeartAttackData"),
                        label: `Z.n. ${t("anamnesis.cardio.myocardial-heart-attack")}`,
                        translationKey: "anamnesis.cardio",
                    }}
                />
                <ConditionalLabelAndValue
                    {...{
                        switchValue: history.getLatestValueOf("haveAtherosclerosis"),
                        value: history.getLatestValueOf("haveAtherosclerosisValue")
                            ? t(`anamnesis.cardio.${history.getLatestValueOf("haveAtherosclerosisValue")}`)
                            : "",
                        label: `Z.n. ${t("anamnesis.cardio.atherosclerosis-of-the-carotids")}`,
                    }}
                />
                <DateAndValueArrayContainer
                    indent
                    yearOnly
                    {...{
                        switchValue: history.getLatestValueOf("haveAtherosclerosisStentImplantation"),
                        array: history.getLatestSubDataRows("haveAtherosclerosisStentImplantationData"),
                        label: t("anamnesis.cardio.stent-or-surgery"),
                        suffix: "stent oder operation",
                    }}
                />
                <DateAndValueArrayContainer
                    yearOnly
                    {...{
                        switchValue: history.getLatestValueOf("haveStroke"),
                        array: history.getLatestSubDataRows("haveStrokeData"),
                        label: `Z.n. ${t("anamnesis.cardio.stroke")}`,
                        translationKey: "anamnesis.cardio",
                    }}
                />
                <ConditionalLabelAndValue
                    {...{
                        switchValue: history.getLatestValueOf("haveStrokePermanentRestriction"),
                        value: history.getLatestValueOf("haveStrokePermanentRestrictionDescription"),
                        label: t("anamnesis.cardio.stroke-permanent-restriction"),
                    }}
                />
                <DateAndValueArrayContainer
                    yearOnly
                    {...{
                        switchValue: history.getLatestValueOf("haveTransientIschemicAttack"),
                        array: history.getLatestSubDataRows("haveTransientIschemicAttackData"),
                        label: `Z.n. ${t("anamnesis.cardio.transient-ischemic-attack")}`,
                        translationKey: "anamnesis.cardio",
                    }}
                />
                <ConditionalLabelAndValue
                    {...{
                        switchValue: history.getLatestValueOf("haveAtherosclerosisLegArteries"),
                        value: history.getLatestValueOf("haveAtherosclerosisLegArteriesStage")
                            ? t(`anamnesis.cardio.${history.getLatestValueOf("haveAtherosclerosisLegArteriesStage")}`)
                            : "",
                        label: `Z.n. ${t("anamnesis.cardio.atherosclerosis-of-the-leg-arteries")}`,
                    }}
                />
                <DateAndValueArrayContainer
                    yearOnly
                    indent
                    {...{
                        switchValue: history.getLatestValueOf("haveAtherosclerosisLegArteriesStentImplantation"),
                        array: history.getLatestSubDataRows("haveAtherosclerosisLegArteriesStentImplantationData"),
                        label: t("anamnesis.cardio.stent-or-surgery"),
                        suffix: "stent oder operation",
                    }}
                />
                <DateAndValueArrayContainer
                    yearOnly
                    {...{
                        switchValue: history.getLatestValueOf("havePlumonaryArteryEmbolism"),
                        array: history.getLatestSubDataRows("havePlumonaryArteryEmbolismData"),
                        label: `Z.n. ${t("anamnesis.cardio.pulmonary-artery-embolism")}`,
                        translationKey: "anamnesis.cardio",
                    }}
                />
                <ConditionalLabelAndValue
                    {...{
                        switchValue: history.getLatestValueOf("haveCardiacEjectionFraction"),
                        value: history.getLatestValueOf("haveCardiacEjectionFractionPercent")
                            ? history.getLatestValueOf("haveCardiacEjectionFractionPercent") + "%"
                            : history.getLatestValueOf("haveCardiacEjectionFractionDescription")
                              ? t(
                                    `anamnesis.cardio.haveCardiacEjectionFractionDescription.${history.getLatestValueOf("haveCardiacEjectionFractionDescription")}`,
                                )
                              : "",
                        label: t("anamnesis.cardio.cardiac-output"),
                    }}
                />
                <ConditionalLabelAndValue
                    {...{
                        switchValue: history.getLatestValueOf("haveCardiomypathy"),
                        value: history.getLatestValueOf("haveCardiomypathyDiagnoseValue")
                            ? t(
                                  `anamnesis.cardio.haveCardiomypathyDiagnoseValue.${history.getLatestValueOf("haveCardiomypathyDiagnoseValue")}`,
                              )
                            : "",
                        label: `Z.n. ${t("anamnesis.cardio.cardiomyopathy")}`,
                    }}
                />
                <ConditionalLabelAndValue
                    {...{
                        switchValue: history.getLatestValueOf("haveSufferFromHeartFailure"),
                        value: history.getLatestValueOf("haveSufferFromHeartFailureDecompensation"),
                        label: `Z.n. ${t("anamnesis.cardio.heart-failure")}`,
                    }}
                />
                <ConditionalLabelAndValue
                    {...{
                        switchValue: history.getLatestValueOf("haveValvularHeartDisease"),
                        value: history.getLatestValueOf("haveValvularHeartDisease") && (
                            <Flex item container grow={0} gap={10} alignItems={"center"}>
                                {[
                                    "hasAorticInsufficiency",
                                    "hasAorticStenosis",
                                    "hasMitralValveInsufficiency",
                                    "hasMitralValveStenosis",
                                    "hasPulmonicValveInsufficiency",
                                    "hasPulmonaryValveStenosis",
                                    "hasTricuspidValveInsufficiency",
                                    "hasTricuspidValveStenosis",
                                ].map((value) =>
                                    history.getLatestValueOf(value) ? (
                                        <Value>
                                            {" " + t(`anamnesis.cardio.valvularHeartDiseaseType.${value}`) + " "}
                                        </Value>
                                    ) : (
                                        <></>
                                    ),
                                )}
                            </Flex>
                        ),
                        label: `Z.n. ${t("anamnesis.cardio.valvular-heart-disease")}`,
                    }}
                />
                <DateAndValueArrayContainer
                    yearOnly
                    {...{
                        switchValue: history.getLatestValueOf("haveHeartValveTreatment"),
                        array: history.getLatestSubDataRows("haveHeartValveTreatmentData"),
                        label: `Z.n. ${t("anamnesis.cardio.heartValveTreatment")}`,
                        getValue: (rows, index) => {
                            return rows
                                .filter((cell) => cell.question !== "date" && cell.answer)
                                .map((cell) => t(`anamnesis.cardio.haveHeartValveTreatmentData.${cell.answer}`))
                                .join(" - ");
                        },
                    }}
                />
                <ConditionalLabelAndValue
                    {...{
                        switchValue: history.getLatestValueOf("haveCardiacArrhythmia"),
                        value: history.getLatestValueOf("haveCardiacArrhythmiaType")
                            ? t(
                                  `anamnesis.cardio.haveCardiacArrhythmiaType.${history.getLatestValueOf("haveCardiacArrhythmiaType")}`,
                              )
                            : "",
                        label: `Z.n. ${t("anamnesis.cardio.cardiac-arrhythmia")}`,
                    }}
                />
                <DateAndValueArrayContainer
                    yearOnly
                    {...{
                        switchValue: history.getLatestValueOf("haveResuscitated"),
                        array: history.getLatestSubDataRows("haveResuscitatedData"),
                        label: t("anamnesis.cardio.RESUSCIATED_DATA"),
                        translationKey: "anamnesis.cardio",
                    }}
                />
                <ConditionalLabelAndValue
                    {...{
                        switchValue: history.getLatestValueOf("haveCongenitalHeartDefect"),
                        value: history.getLatestValueOf("haveCongenitalHeartDefectType")
                            ? t(`anamnesis.cardio.${history.getLatestValueOf("haveCongenitalHeartDefectType")}`)
                            : "",
                        label: `Z.n. ${t("anamnesis.cardio.congenital-heart-defect")}`,
                    }}
                />
                <DateAndValueArrayContainer
                    yearOnly
                    {...{
                        switchValue: history.getLatestValueOf("havePacemakerOrDefibrillator"),
                        array: history.getLatestSubDataRows("havePacemakerOrDefibrillatorData"),
                        label: t("anamnesis.cardio.pacemaker-or-defibrillator"),
                        getValue: (rows, index) => {
                            return rows
                                .filter((cell) => cell.question !== "date" && cell.answer)
                                .map((cell) => t(`anamnesis.cardio.havePacemakerOrDefibrillatorData.${cell.answer}`))
                                .join(" - ");
                        },
                    }}
                />
                <DateAndValueArrayContainer
                    yearOnly
                    {...{
                        switchValue: history.getLatestValueOf("haveMyocardialBiopsy"),
                        array: history.getLatestSubDataRows("haveMyocardialBiopsyData"),
                        label: t("anamnesis.cardio.myocardial-biopsy"),
                        translationKey: "anamnesis.cardio",
                    }}
                />
            </Flex>
        </HistoryContainerAndTitle>
    );
};

export default CardiovascularDiseaseHistory;
